import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [event, setEvent] = useState(() => {
    const stored = localStorage.getItem('eventInfo');
    return stored ? JSON.parse(stored) : null;
  });
  const [permissions, setPermissions] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      // If attendee, also set event info, permissions, and committees from response
      if (response.data.event) {
        setEvent(response.data.event);
        localStorage.setItem('eventInfo', JSON.stringify(response.data.event));
      }
      if (response.data.permissions) {
        setPermissions(response.data.permissions);
      }
      if (response.data.committees) {
        setCommittees(response.data.committees);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // New setAuth function for direct auth setting
  const setAuth = (userData, newToken, eventData = null) => {
    setUser(userData);
    setToken(newToken);
    if (eventData) {
      setEvent(eventData);
      localStorage.setItem('eventInfo', JSON.stringify(eventData));
    }
  };

  // Organizer login
  const loginOrganizer = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login/organizer`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  // Organizer register
  const registerOrganizer = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register/organizer`, userData);
    const { token: newToken, user: newUser } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(newUser);
    return newUser;
  };

  // Attendee login
  const loginAttendee = async (email, password, eventCode) => {
    const response = await axios.post(`${API_URL}/api/auth/login/attendee`, { 
      email, 
      password, 
      event_code: eventCode 
    });
    const { token: newToken, user: userData, event: eventData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('eventInfo', JSON.stringify(eventData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setEvent(eventData);
    return { user: userData, event: eventData };
  };

  // Attendee register
  const registerAttendee = async (userData, eventCode) => {
    const response = await axios.post(`${API_URL}/api/auth/register/attendee`, {
      ...userData,
      event_code: eventCode
    });
    const { token: newToken, user: newUser, event: eventData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('eventInfo', JSON.stringify(eventData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(newUser);
    setEvent(eventData);
    return { user: newUser, event: eventData };
  };

  // Verify event code
  const verifyEventCode = async (code) => {
    const response = await axios.get(`${API_URL}/api/auth/verify-code/${code}`);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('eventInfo');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setEvent(null);
    setPermissions([]);
    setCommittees([]);
  };

  const updateUser = (updatedData) => {
    setUser(prev => ({ ...prev, ...updatedData }));
  };

  // Role checks
  const isAdmin = () => user?.role === 'admin';
  const isOrganizer = () => user?.role === 'organizer';
  const isAttendee = () => user?.role === 'attendee';

  // Permission checks for committee-based access
  const hasPermission = (permission) => {
    if (isOrganizer()) return true; // Organizers have all permissions
    return permissions.includes('all') || permissions.includes(permission);
  };

  // Check if user can post announcements
  const canPostAnnouncements = () => hasPermission('post_announcements');
  
  // Check if user can manage payments (view all + verify)
  const canManagePayments = () => hasPermission('view_all_payments') || hasPermission('verify_payments');
  
  // Check if user can verify payments
  const canVerifyPayments = () => hasPermission('verify_payments');
  
  // Check if user can upload documents
  const canUploadDocuments = () => hasPermission('upload_documents');
  
  // Check if user can manage programs
  const canManagePrograms = () => hasPermission('manage_programs');

  // Check if user is in a specific committee
  const isInCommittee = (committeeName) => {
    return committees.some(c => c.name.toLowerCase().includes(committeeName.toLowerCase()));
  };

  // Check if user is head of a specific committee
  const isCommitteeHead = (committeeName) => {
    return committees.some(c => c.name.toLowerCase().includes(committeeName.toLowerCase()) && c.is_head);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      event,
      permissions,
      committees,
      loading,
      setAuth,
      loginOrganizer,
      registerOrganizer,
      loginAttendee,
      registerAttendee,
      verifyEventCode,
      logout,
      updateUser,
      isAdmin,
      isOrganizer,
      isAttendee,
      hasPermission,
      canPostAnnouncements,
      canManagePayments,
      canVerifyPayments,
      canUploadDocuments,
      canManagePrograms,
      isInCommittee,
      isCommitteeHead
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
